import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  CheckCircle,
  AlertCircle,
  Users,
  IndianRupee,
  Target,
  RefreshCw,
  BarChart3,
  Filter,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Zap,
  Plus,
  Shield
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { DashboardLayout } from '@/components/DashboardLayout';
import { formatCurrency } from '@/lib/utils';
// import { RevenueTargetWidget } from '@/components/dashboard/RevenueTargetWidget'; // Temporarily disabled due to eslint false positive
import { RecentActivityFeed } from '@/components/dashboard/RecentActivityFeed';
import { UntouchedLeadsAlert } from '@/components/dashboard/UntouchedLeadsAlert';
import { FinancePulseWidget } from '@/components/dashboard/FinancePulseWidget';

interface LeadMetrics {
  closures: {
    expectedToday: { count: number; value: number };
    expectedTomorrow: { count: number; value: number };
    closedToday: { count: number; value: number };
    closedThisWeek: { count: number; value: number };
    missed: { count: number; value: number };
  };
  followUps: {
    today: { count: number; percentage: number };
    tomorrow: { count: number; percentage: number };
    missed: { count: number; percentage: number };
  };
  volume: {
    allLeads: number;
    allFollowUps: number;
    addedToday: number;
    upcoming: number;
    selfAssigned: number;
    assignedByOthers: number;
    reassignedToday: number;
  };
  value: {
    total: number;
    converted: number;
    convertedToday: number;
    addedToday: number;
    assignedToday: number;
    highestDeal: number;
    avgDealSize: number;
    dealsOver50K: number;
  };
  funnel: {
    freshLead: { count: number; value: number };
    demoBooked: { count: number; value: number };
    proposalSent: { count: number; value: number };
    followUpOngoing: { count: number; value: number };
    negotiation: { count: number; value: number };
    closedWon: { count: number; value: number };
    closedLost: { count: number; value: number };
  };
  performance: {
    executives: Array<{
      id: string;
      name: string;
      leadCount: number;
      pipelineValue: number;
      conversionRate: number;
      avgClosureDays: number;
    }>;
  };
}

interface Filters {
  timeRange: string;
  stage: string;
  priority: string;
  brand: string;
  creator: string;
}

// Executive Performance API response types
interface ExecutivePerformanceData {
  id: string;
  name: string;
  email: string;
  role: string;
  leads: {
    total: number;
    createdThisMonth: number;
    active: number;
    assigned: number;
    created: number;
  };
  conversions: {
    won: number;
    wonThisMonth: number;
    lost: number;
    lostThisMonth: number;
    conversionRate: number;
  };
  revenue: {
    thisMonth: number;
    total: number;
    pipelineValue: number;
    avgDealSize: number;
  };
  efficiency: {
    avgDaysToClose: number;
    leadsPerDay: number | string;
  };
  lastActive: string;
  lastLogin: string;
}

interface ExecutivePerformanceResponse {
  success: boolean;
  data: ExecutivePerformanceData[];
  summary: {
    totalExecutives: number;
    totalLeadsManaged: number;
    totalRevenueThisMonth: number;
    totalPipelineValue: number;
    avgConversionRate: number;
  };
}

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'green' | 'blue' | 'orange' | 'red' | 'gray' | 'purple';
  onClick?: () => void;
  isLoading?: boolean;
  trend?: 'up' | 'down' | 'neutral';
  tooltip?: string;
}> = ({ title, value, subtitle, icon, color, onClick, isLoading, trend, tooltip }) => {
  const colorClasses = {
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700'
  };

  const iconClasses = {
    green: 'text-green-600',
    blue: 'text-blue-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
    gray: 'text-gray-600',
    purple: 'text-purple-600'
  };

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${colorClasses[color]} ${onClick ? 'hover:border-opacity-80' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      aria-label={tooltip || `${title}: ${value}`}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 rounded-lg ${iconClasses[color]}`}>
                {icon}
              </div>
              {trend && (
                <div className={`flex items-center gap-1 text-sm ${
                  trend === 'up' ? 'text-green-600' : 
                  trend === 'down' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {trend === 'up' ? <TrendingUp size={14} /> : 
                   trend === 'down' ? <TrendingDown size={14} /> : 
                   <TrendingUp size={14} />}
                </div>
              )}
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            )}
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PerformanceTable: React.FC<{
  data: ExecutivePerformanceData[];
  isLoading?: boolean;
  onExecutiveClick?: (id: string) => void;
}> = ({ data, isLoading, onExecutiveClick }) => {
  const [sortBy, setSortBy] = useState<'name' | 'leadCount' | 'pipelineValue' | 'conversionRate' | 'avgClosureDays'>('pipelineValue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Map the new API structure to sortable values
  const getMappedValue = (executive: ExecutivePerformanceData, field: typeof sortBy): number | string => {
    switch (field) {
      case 'name':
        return executive.name;
      case 'leadCount':
        return executive.leads.total;
      case 'pipelineValue':
        return executive.revenue.pipelineValue;
      case 'conversionRate':
        return executive.conversions.conversionRate;
      case 'avgClosureDays':
        return executive.efficiency.avgDaysToClose;
      default:
        return 0;
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = getMappedValue(a, sortBy);
    const bValue = getMappedValue(b, sortBy);

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Executive Performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">No executive data available</p>
            <p className="text-xs mt-1">Executive performance data will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th
                    className="text-left p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('name')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort('name'); } }}
                    tabIndex={0}
                    role="columnheader"
                    aria-sort={sortBy === 'name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortBy === 'name' && (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-left p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('leadCount')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort('leadCount'); } }}
                    tabIndex={0}
                    role="columnheader"
                    aria-sort={sortBy === 'leadCount' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Leads
                      {sortBy === 'leadCount' && (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-left p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('pipelineValue')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort('pipelineValue'); } }}
                    tabIndex={0}
                    role="columnheader"
                    aria-sort={sortBy === 'pipelineValue' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Pipeline Value
                      {sortBy === 'pipelineValue' && (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-left p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionRate')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort('conversionRate'); } }}
                    tabIndex={0}
                    role="columnheader"
                    aria-sort={sortBy === 'conversionRate' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Conversion Rate
                      {sortBy === 'conversionRate' && (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-left p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('avgClosureDays')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort('avgClosureDays'); } }}
                    tabIndex={0}
                    role="columnheader"
                    aria-sort={sortBy === 'avgClosureDays' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Avg Closure Days
                      {sortBy === 'avgClosureDays' && (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((executive) => (
                  <tr key={executive.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <button
                        onClick={() => onExecutiveClick?.(executive.id)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                      >
                        {executive.name}
                      </button>
                    </td>
                    <td className="p-3">{executive.leads.total.toLocaleString('en-IN')}</td>
                    <td className="p-3">{formatCurrency(executive.revenue.pipelineValue)}</td>
                    <td className="p-3">{Number(executive.conversions.conversionRate || 0).toFixed(1)}%</td>
                    <td className="p-3">{executive.efficiency.avgDaysToClose} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [filters, setFilters] = useState<Filters>({
    timeRange: 'all',
    stage: 'all',
    priority: 'all',
    brand: 'all',
    creator: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch dashboard data
  const { data: metrics, isLoading, isFetching, error: metricsError, refetch } = useQuery({
    queryKey: ['dashboard-metrics', filters],
    queryFn: async (): Promise<LeadMetrics> => {
      return await api.getLeadMetrics(user?.role, user?.id, filters);
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: 2,
  });

  // Fetch executive performance data (only for CEO)
  const apiBaseUrl = process.env.REACT_APP_API_URL || '/api';
  const { data: executivePerformance, isLoading: isLoadingExecutives, error: execError, refetch: refetchExecutives } = useQuery({
    queryKey: ['executive-performance'],
    queryFn: async (): Promise<ExecutivePerformanceData[]> => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiBaseUrl}/executive-performance`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        console.warn('Executive performance API failed, returning empty array');
        return [];
      }

      const result: ExecutivePerformanceResponse = await response.json();

      if (result.success && result.data) {
        return result.data;
      }

      return [];
    },
    enabled: user?.role === 'CEO', // Only fetch for CEO
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Convert active timeRange filter to ISO date strings for pipeline navigation
  const getDateRange = (): { dateFrom?: string; dateTo?: string } => {
    const now = new Date();
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    if (filters.timeRange === '1d') {
      const from = new Date(now); from.setDate(from.getDate() - 1);
      return { dateFrom: fmt(from), dateTo: fmt(now) };
    } else if (filters.timeRange === '7d') {
      const from = new Date(now); from.setDate(from.getDate() - 7);
      return { dateFrom: fmt(from), dateTo: fmt(now) };
    } else if (filters.timeRange === '30d') {
      const from = new Date(now); from.setDate(from.getDate() - 30);
      return { dateFrom: fmt(from), dateTo: fmt(now) };
    } else if (filters.timeRange === '90d') {
      const from = new Date(now); from.setDate(from.getDate() - 90);
      return { dateFrom: fmt(from), dateTo: fmt(now) };
    }
    return {};
  };

  const handleRefresh = () => {
    refetch();
    if (user?.role === 'CEO') {
      refetchExecutives();
    }
    toast({
      title: "Dashboard refreshed",
      description: "Latest data has been loaded",
    });
  };

  const handleExecutiveClick = (executiveId: string) => {
    navigate(`/executive-performance/${executiveId}`);
  };

  const handleCardClick = (filter: string) => {
    // Navigate to pipeline with specific filter
    navigate(`/pipeline?filter=${filter}`);
  };

  const handleNavigate = (page: string) => {
    navigate(page);
  };

  if (!user) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Untouched Leads Alert - Shows leads not touched in 24+ hours */}
        <UntouchedLeadsAlert />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Lead Intelligence Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back, {user.name || 'User'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              aria-expanded={showFilters}
            >
              <Filter className="h-4 w-4" />
              Filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center gap-2"
              aria-label="Refresh dashboard data"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white p-4 rounded-lg border"
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Select value={filters.timeRange} onValueChange={(value: string) => setFilters({...filters, timeRange: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Time Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1d">Last 24 hours</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.stage} onValueChange={(value: string) => setFilters({...filters, stage: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="fresh">Fresh Lead</SelectItem>
                  <SelectItem value="demo">Demo Booked</SelectItem>
                  <SelectItem value="proposal">Proposal Sent</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.priority} onValueChange={(value: string) => setFilters({...filters, priority: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.brand} onValueChange={(value: string) => setFilters({...filters, brand: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  <SelectItem value="brand1">Brand 1</SelectItem>
                  <SelectItem value="brand2">Brand 2</SelectItem>
                  <SelectItem value="brand3">Brand 3</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.creator} onValueChange={(value: string) => setFilters({...filters, creator: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Creator" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Creators</SelectItem>
                  <SelectItem value="user1">User 1</SelectItem>
                  <SelectItem value="user2">User 2</SelectItem>
                  <SelectItem value="user3">User 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        )}

        {/* Error State */}
        {metricsError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" role="alert">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Failed to load dashboard data</p>
              <p className="text-xs text-red-600 mt-0.5">Please check your connection and try again.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="text-red-700 border-red-300 hover:bg-red-100">
              Retry
            </Button>
          </div>
        )}

        {/* Background Refetch Indicator */}
        {isFetching && !isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500" role="status" aria-live="polite">
            <RefreshCw className="h-3 w-3 animate-spin" />
            <span>Updating...</span>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Pipeline Value"
            value={isLoading ? '...' : formatCurrency(metrics?.value.total || 0)}
            subtitle="All active leads"
            icon={<IndianRupee className="h-5 w-5" />}
            color="blue"
            onClick={() => handleCardClick('pipeline')}
            isLoading={isLoading}
            trend="up"
          />
          
          <MetricCard
            title="Leads Added Today"
            value={isLoading ? '...' : metrics?.volume.addedToday || 0}
            subtitle="New leads this week"
            icon={<Plus className="h-5 w-5" />}
            color="green"
            onClick={() => handleCardClick('new')}
            isLoading={isLoading}
            trend="up"
          />
          
          <MetricCard
            title="Follow-ups Today"
            value={isLoading ? '...' : metrics?.followUps.today.count || 0}
            subtitle={`${metrics?.followUps.today.percentage || 0}% completion`}
            icon={<Calendar className="h-5 w-5" />}
            color="orange"
            onClick={() => handleCardClick('followup')}
            isLoading={isLoading}
            trend="neutral"
          />
          
          <MetricCard
            title="Expected Closures"
            value={isLoading ? '...' : metrics?.closures.expectedToday.count || 0}
            subtitle={`${formatCurrency(metrics?.closures.expectedToday.value || 0)} value`}
            icon={<Target className="h-5 w-5" />}
            color="purple"
            onClick={() => handleCardClick('closing')}
            isLoading={isLoading}
            trend="up"
          />
        </div>

        {/* Secondary Metrics Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard
            title="All Active Leads"
            value={isLoading ? '...' : (metrics?.volume.allLeads || 0).toLocaleString('en-IN')}
            icon={<Users className="h-4 w-4" />}
            color="gray"
            isLoading={isLoading}
            onClick={() => handleCardClick('all')}
          />
          <MetricCard
            title="Closed This Week"
            value={isLoading ? '...' : metrics?.closures.closedThisWeek.count || 0}
            subtitle={formatCurrency(metrics?.closures.closedThisWeek.value || 0)}
            icon={<CheckCircle className="h-4 w-4" />}
            color="green"
            isLoading={isLoading}
            onClick={() => handleCardClick('won')}
            tooltip="View deals closed this week"
          />
          <MetricCard
            title="Missed Closures"
            value={isLoading ? '...' : metrics?.closures.missed.count || 0}
            subtitle={formatCurrency(metrics?.closures.missed.value || 0)}
            icon={<AlertCircle className="h-4 w-4" />}
            color="red"
            isLoading={isLoading}
            trend={metrics?.closures.missed.count ? 'down' : undefined}
            onClick={() => handleCardClick('missed')}
            tooltip="View missed closure leads"
          />
          <MetricCard
            title="Missed Follow-ups"
            value={isLoading ? '...' : metrics?.followUps.missed.count || 0}
            subtitle={`${metrics?.followUps.missed.percentage || 0}% of total`}
            icon={<AlertCircle className="h-4 w-4" />}
            color="orange"
            isLoading={isLoading}
            onClick={() => handleCardClick('missed-followup')}
            tooltip="View leads with missed follow-ups"
          />
          <MetricCard
            title="Avg Deal Size"
            value={isLoading ? '...' : formatCurrency(metrics?.value.avgDealSize || 0)}
            icon={<IndianRupee className="h-4 w-4" />}
            color="blue"
            isLoading={isLoading}
          />
          <MetricCard
            title="Highest Deal"
            value={isLoading ? '...' : formatCurrency(metrics?.value.highestDeal || 0)}
            icon={<TrendingUp className="h-4 w-4" />}
            color="purple"
            isLoading={isLoading}
          />
        </div>

        {/* Finance Pulse — CEO/Group Manager only */}
        {(user?.role === 'CEO' || user?.role === 'GROUP_MANAGER') && (
          <FinancePulseWidget />
        )}

        {/* Funnel Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Sales Funnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Skeleton className="w-3 h-3 rounded-full" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <div className="text-right space-y-1">
                        <Skeleton className="h-4 w-16 ml-auto" />
                        <Skeleton className="h-3 w-12 ml-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : metrics?.funnel && Object.values(metrics.funnel).some(data => data.count > 0) ? (
                <div className="space-y-4">
                  {Object.entries(metrics.funnel).map(([stage, data]) => {
                    const stageFilterMap: Record<string, string> = {
                      freshLead: 'NEW', demoBooked: 'CONTACTED', proposalSent: 'PROPOSAL',
                      followUpOngoing: 'DISCUSSION', negotiation: 'NEGOTIATION',
                      closedWon: 'WON', closedLost: 'LOST'
                    };
                    const funnelStageColors: Record<string, string> = {
                      freshLead: 'bg-blue-500',
                      demoBooked: 'bg-cyan-500',
                      followUpOngoing: 'bg-amber-500',
                      proposalSent: 'bg-purple-500',
                      negotiation: 'bg-orange-500',
                      closedWon: 'bg-green-500',
                      closedLost: 'bg-red-500',
                    };
                    return (
                    <button
                      key={stage}
                      onClick={() => {
                        const range = getDateRange();
                        const params = new URLSearchParams({ stage: stageFilterMap[stage] || stage });
                        if (range.dateFrom) params.set('entryDateFrom', range.dateFrom);
                        if (range.dateTo) params.set('entryDateTo', range.dateTo);
                        navigate(`/pipeline?${params.toString()}`);
                      }}
                      className="flex items-center justify-between w-full text-left rounded-md px-2 py-1.5 hover:bg-gray-50 transition-colors cursor-pointer group"
                      aria-label={`View ${stage.replace(/([A-Z])/g, ' $1').trim()} leads in pipeline`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${funnelStageColors[stage] || 'bg-gray-500'}`}></div>
                        <span className="text-sm font-medium capitalize group-hover:text-blue-600 transition-colors">
                          {stage.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{data.count} leads</div>
                        <div className="text-xs text-gray-500">{formatCurrency(data.value)}</div>
                      </div>
                    </button>
                  );})}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm font-medium">No leads in your pipeline yet</p>
                  <p className="text-xs mt-1">Start by adding leads to track your sales funnel</p>
                </div>
              )}
            </CardContent>
          </Card>

          <RecentActivityFeed />
        </div>

        {/* Performance Table - Only show for CEO */}
        {user?.role === 'CEO' && execError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3" role="alert">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 flex-1">Failed to load executive performance data</p>
            <Button variant="outline" size="sm" onClick={() => refetchExecutives()} className="text-red-700 border-red-300 hover:bg-red-100">Retry</Button>
          </div>
        )}
        {user?.role === 'CEO' && (
          <PerformanceTable
            data={executivePerformance || []}
            isLoading={isLoadingExecutives}
            onExecutiveClick={handleExecutiveClick}
          />
        )}

        {/* Revenue Target Widget */}
        {/* <RevenueTargetWidget /> */} {/* Temporarily disabled due to eslint false positive */}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-20"
                onClick={() => navigate('/pipeline')}
                aria-label="Navigate to pipeline to add a new lead"
              >
                <Plus className="h-5 w-5" />
                <span className="text-sm">Add Lead</span>
              </Button>

              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-20"
                onClick={() => navigate('/tasks')}
                aria-label="Navigate to tasks to create a new task"
              >
                <Calendar className="h-5 w-5" />
                <span className="text-sm">Create Task</span>
              </Button>

              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-20"
                onClick={() => navigate('/whatsapp-management')}
                aria-label="Navigate to WhatsApp management to send a message"
              >
                <MessageSquare className="h-5 w-5" />
                <span className="text-sm">Send Message</span>
              </Button>

              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-20"
                onClick={() => navigate('/risk-assessment')}
                aria-label="Navigate to risk assessment dashboard"
              >
                <Shield className="h-5 w-5" />
                <span className="text-sm">Risk Assessment</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}; 